<script setup lang="ts">
import { useFetch, useRuntimeConfig, useState } from '#app'
import { defineAsyncComponent, onMounted, shallowRef, watch, type Component } from '#imports'
import { usePath } from '~/layers/cms/app/composables/usePath'
import { default_page_settings, type CmsData } from '~/layers/cms/app/types/cmsTypes'
import { useAuthStore } from './layers/cms/app/stores/authStore'
import { useCmsEditStore } from './layers/cms/app/stores/cmsEditStore'
import { getCmsData, getGlobalCmsData } from './layers/cms/app/composables/cms'

const config = useRuntimeConfig()
const authStore = useAuthStore()
const path = usePath()
const editStore = useCmsEditStore()

const [{ data }, { data: globalData }] = await Promise.all([
  useFetch<Partial<CmsData>>(`${config.public.apiBase}/api/get-cms-data`, {
    baseURL: config.public.apiBase,
    params: { slug: path },
    watch: [path],
    server: true,
    lazy: false,
    default: () => ({ settings: default_page_settings } as Partial<CmsData>)
  }),
  useFetch<CmsData>(`${config.public.apiBase}/api/get-global-cms-data`, {
    baseURL: config.public.apiBase,
    server: true,
    lazy: false
  })
])

const cmsData = useState<CmsData>('cmsData', () => ({
  ...data.value,
  ...globalData.value
} as CmsData))

watch([data, globalData], async ([newData, newGlobal]) => {
  cmsData.value = { ...newData, ...newGlobal } as CmsData
})

async function refreshAll() {
  editStore.clear()
  const [newData, newGlobal] = await Promise.all([
    getCmsData(path.value),
    getGlobalCmsData()
  ])
  data.value = newData.data as CmsData
  globalData.value = newGlobal.data as CmsData
  cmsData.value = { ...newData.data as CmsData, ...newGlobal.data as CmsData } as CmsData
}

if (authStore.authorized) {
  watch(path, async () => refreshAll())
}

const adminThings = shallowRef<Component>()

onMounted(async () => {
  if (authStore.authorized) {
    await refreshAll()
    adminThings.value = defineAsyncComponent(() => import('~/layers/cms/app/components/cms/admin/adminThings.vue'))
  }
})
</script>

<template>
  <NuxtLayout>
    <CmsSEO />
    <CmsAdminStuff />
    <DynamicDialog />
    <Toast />
    <NuxtPage />
  </NuxtLayout>
</template>
